// Companion/blocks/legacyChatBlocks.ts
//
// Old conversations still open, and the blocks they showed come back with them
// (spec, "Legacy conversations": restored read-only from persisted UI state, no
// migration). The old panel persisted a `PersistedToolBlock` per open block —
// a legacy `kind` plus the raw `item`/`items` search hit it was drawn from.
//
// This maps one of those onto the block contract in `types/chatBlocks.ts`, so
// history renders through the same registry as a live turn instead of needing a
// second set of components. The block is marked legacy when it is hydrated,
// which is what makes it inert.
//
// One-way and lossy on purpose: a legacy kind with no modern counterpart
// (`practice_weakest`, `review_due_flashcards` — the launcher blocks the spec
// turned into navigate follow-ups) maps to nothing and is dropped, because
// there is no content in it to show.

import type { PersistedToolBlock } from '@/types/legacyChat';
import type { ChatHydratedBlock } from '@/slices/chat/chatSlice';
import type { ChatBlockKind } from '@/types/chatBlocks';
import { asRecordArray } from './chatBlockPayload';

const KIND_BY_LEGACY_KIND: Record<string, ChatBlockKind> = {
  practice_question: 'question',
  flashcards: 'flashcards',
  watch_video: 'video',
  article: 'article',
  read_full_doc: 'help_doc',
  open_dashboard: 'dashboard',
  document: 'document',
};

type LegacyItem = Record<string, unknown>;

const str = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const items = (block: PersistedToolBlock): LegacyItem[] =>
  asRecordArray(block.items);

const legacyOptions = (item: LegacyItem) =>
  asRecordArray(item.options).map((option) => ({
    letter: str(option.letter) || undefined,
    text: str(option.text),
    explanation: str(option.explanation_main) || null,
    ...(typeof option.is_correct === 'boolean'
      ? { isCorrect: option.is_correct }
      : {}),
  }));

/** The letter the student picked, as the old anchor recorded it. */
const legacySelectedLetter = (block: PersistedToolBlock): string | null => {
  const vignette = (block.anchor?.vignette ?? null) as LegacyItem | null;
  return vignette ? str(vignette.user_selected_letter) || null : null;
};

const toPayload = (
  kind: ChatBlockKind,
  block: PersistedToolBlock
): Record<string, unknown> | null => {
  const item = (block.item ?? {}) as LegacyItem;

  switch (kind) {
    case 'question':
      return str(item.id)
        ? {
            vignetteId: str(item.id),
            variantId: str(item.variant_id) || null,
            stem: str(item.question_stem),
            correctLetter: str(item.correct_letter) || null,
            explanation: str(item.correct_explanation) || null,
            options: legacyOptions(item),
            selectedLetter: legacySelectedLetter(block),
          }
        : null;

    case 'flashcards': {
      const cards = items(block)
        .filter((card) => str(card.id) !== '')
        .map((card) => ({
          id: str(card.id),
          front: str(card.front),
          back: str(card.back),
          explanation: str(card.explanation_short) || null,
        }));

      return cards.length > 0 ? { cards } : null;
    }

    case 'video':
      return str(item.id)
        ? {
            videoId: str(item.id),
            title: str(item.title) || null,
            description: str(item.description) || null,
            thumbnailUrl: str(item.thumbnailUrl) || null,
          }
        : null;

    case 'article': {
      // The old block carried either a resolvable id/slug or an embedded
      // article; only the former can be re-fetched, and only real articles were
      // ever rendered, so an embedded-only block is dropped.
      const article = (item.article ?? null) as LegacyItem | null;

      const id =
        str(item.articleIdOrSlug) ||
        (article ? str(article.id) || str(article.slug) : '');

      return id
        ? { articleId: id, title: article ? str(article.title) || null : null }
        : null;
    }

    case 'help_doc': {
      const slug = str(item.slug) || str(item.doc_slug);

      return slug
        ? {
            slug,
            title: str(item.title) || null,
            content: str(item.content) || null,
          }
        : null;
    }

    case 'dashboard': {
      // The old dashboard block drew from the whole turn result, which is not
      // persisted. What survives is the block itself, so it restores as an
      // empty performance card rather than as numbers from another day.
      const qbank = (item.qbank ?? null) as LegacyItem | null;

      return {
        view: 'exam',
        examName: str(item.active_exam) || null,
        qbank: qbank
          ? {
              accuracy: num(qbank.overall_accuracy),
              completed: num(qbank.overall_total),
            }
          : null,
      };
    }

    case 'document':
      return str(item.lectureId)
        ? {
            lectureId: str(item.lectureId),
            fileName: str(item.fileName) || null,
          }
        : null;

    default:
      return null;
  }
};

/**
 * One persisted legacy tool block as a hydratable block, or null when it holds
 * nothing this client can draw. Pass the results to `conversationHydrated` on
 * the message they belonged to, with `legacy: true`.
 */
export const legacyToolBlockToChatBlock = (
  block: PersistedToolBlock
): ChatHydratedBlock | null => {
  const kind = KIND_BY_LEGACY_KIND[block.kind];
  if (!kind) return null;

  const payload = toPayload(kind, block);
  if (!payload) return null;

  return {
    id: block.id,
    kind,
    // The old panel appended blocks under the answer they belonged to.
    placement: 'end',
    payload,
    // The old blocks recorded no target, and inventing one from an id would
    // claim the conversation offered navigation that it never did.
    target: null,
  };
};

/** Every legacy block of one message that can still be drawn, in order. */
export const legacyToolBlocksToChatBlocks = (
  blocks: PersistedToolBlock[]
): ChatHydratedBlock[] =>
  blocks.flatMap((block) => {
    const mapped = legacyToolBlockToChatBlock(block);
    return mapped ? [mapped] : [];
  });
